import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';

const ISOContext = createContext();

const ISO_ENDPOINTS = {
  // Response endpoints
  saveResponses: `https://1quuxn0io4.execute-api.ap-southeast-2.amazonaws.com/prod/api/save-responses`,
  getResponses: `https://nevu2xymue.execute-api.ap-southeast-2.amazonaws.com/prod/api/get-responses`,
  
  // File endpoints
  uploadFile: `https://bfkv6ex3qg.execute-api.ap-southeast-2.amazonaws.com/prod/api/iso/upload-file`,
  saveFileMetadata: `https://z68kc6t3nf.execute-api.ap-southeast-2.amazonaws.com/prod/api/save-file-metadata`,
  getFileMetadata: `https://nhzsgifxm4.execute-api.ap-southeast-2.amazonaws.com/prod/api/get-file-metadata`,
  deleteFile: `https://2pm8jqhrug.execute-api.ap-southeast-2.amazonaws.com/prod/api/delete-file`,
  
  // Comment endpoint
  saveComment: `https://api.example.com/iso/save-comment`, // Fixed URL
};

const logISO = (message, data = null) => {
  if (data) {
    console.log(`🟢 ISO LOG: ${message}`, data);
  } else {
    console.log(`🟢 ISO LOG: ${message}`);
  }
};

export const ISOProvider = ({ children, userData }) => {
  // Extract userId from userData
  const userId = userData?.["cognito:username"] || null;

  // State Management
  const [isoResponses, setIsoResponses] = useState({
    Organizational: {},
    People: {},
    Physical: {},
    Technological: {},
  });

  const [evidenceFiles, setEvidenceFiles] = useState({
    Organizational: {},
    People: {},
    Physical: {},
    Technological: {},
  });

  const [policyFiles, setPolicyFiles] = useState({
    Organizational: {},
    People: {},
    Physical: {},
    Technological: {},
  });

  const [isoScores, setIsoScores] = useState({
    Organizational: 0,
    People: 0,
    Physical: 0,
    Technological: 0,
    overall: 0,
  });

  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Helper: Get User Region
  const getUserRegion = () => {
    return sessionStorage.getItem("userRegion") || "GB";
  };

  // ==================== FETCH ALL ISO DATA ====================
  const fetchAllISOData = useCallback(async () => {
    if (!userId) {
      console.warn("⚠️ No userId available to fetch ISO data");
      return;
    }
    
    setLoading(true);
    console.log("🔄 Fetching all ISO data for user:", userId);

    try {
      // Fetch responses and files in parallel
      const [responsesRes, filesRes] = await Promise.all([
        axios.get(ISO_ENDPOINTS.getResponses, {
          params: { userId },
          headers: { Region: getUserRegion() },
        }),
        axios.get(ISO_ENDPOINTS.getFileMetadata, {
          params: { userId },
          headers: { Region: getUserRegion() },
        }),
      ]);

      // Update responses
      if (responsesRes.data?.success) {
        setIsoResponses(responsesRes.data.responses || {
          Organizational: {},
          People: {},
          Physical: {},
          Technological: {},
        });
        
        setIsoScores(responsesRes.data.scores || {
          Organizational: 0,
          People: 0,
          Physical: 0,
          Technological: 0,
          overall: 0,
        });

        console.log("✅ Responses loaded:", responsesRes.data.responses);
      }

      // Update file metadata
      if (filesRes.data?.success) {
        setEvidenceFiles(filesRes.data.evidenceFiles || {
          Organizational: {},
          People: {},
          Physical: {},
          Technological: {},
        });
        
        setPolicyFiles(filesRes.data.policyFiles || {
          Organizational: {},
          People: {},
          Physical: {},
          Technological: {},
        });

        console.log("✅ Files loaded:", filesRes.data);
      }

      setDataLoaded(true);
      
      // Backup to localStorage
      saveToLocalStorage(userId, {
        responses: responsesRes.data.responses,
        evidenceFiles: filesRes.data.evidenceFiles,
        policyFiles: filesRes.data.policyFiles,
        scores: responsesRes.data.scores,
      });

    } catch (error) {
      console.error("❌ Error fetching ISO data:", error);
      
      // Fallback to localStorage
      console.log("🔄 Loading from localStorage...");
      loadFromLocalStorage(userId);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Auto-fetch data when userId becomes available
  useEffect(() => {
    if (userId && !dataLoaded) {
      fetchAllISOData();
    }
  }, [userId, dataLoaded, fetchAllISOData]);

  // ==================== SAVE RESPONSE (YES/NO/NA) ====================
  const saveISOResponse = useCallback(
    async (section, questionId, answer, questionText, isoClause, controlId, severity, description) => {
      if (!userId || !section) {
        console.warn("⚠️ Missing userId or section", { userId, section });
        return;
      }

      logISO("Answer clicked", {
        userId,
        section,
        questionId,
        answer,
        questionText,
      });

      try {
        const payload = {
          userId,
          section,
          questionId,
          answer,
          questionText,
          isoClause,        // e.g., "A.5.1"
          controlId,        // e.g., "A.5.1.1"
          severity,         // e.g., "HIGH", "INFORMATIONAL"
          description,      // question description
          timestamp: new Date().toISOString(),
        };

        logISO("Sending payload to backend", payload);

        const response = await axios.post(
          ISO_ENDPOINTS.saveResponses,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Region: getUserRegion(),
            },
          }
        );

        logISO("Backend response received", response.data);

        // Update local state
        setIsoResponses((prev) => {
          const updated = {
            ...prev,
            [section]: {
              ...prev[section],
              [questionId]: {
                questionId,
                answer,
                questionText,
                isoClause,
                controlId,
                severity,
              },
            },
          };

          logISO("Local state updated", updated[section][questionId]);
          return updated;
        });

        // Update score
        calculateAndUpdateScore(section, questionId, answer);

        logISO("Score recalculated", { section, answer });

      } catch (error) {
        console.error("❌ Error saving ISO response", {
          message: error.message,
          response: error.response?.data,
          payload: {
            userId,
            section,
            questionId,
            answer,
          },
        });
      }
    },
    [userId, isoResponses]
  );

  // ==================== UPLOAD FILE (EVIDENCE/POLICY) ====================
  const uploadISOFile = useCallback(async (section, questionId, file, fileType) => {
    if (!userId || !section || !file) {
      console.warn("⚠️ Missing required parameters for file upload");
      return null;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", userId);
      formData.append("section", section);
      formData.append("questionId", questionId);
      formData.append("fileType", fileType); // "evidence" or "policy"

      const uploadRes = await fetch(ISO_ENDPOINTS.uploadFile, {
        method: "POST",
        headers: { Region: getUserRegion() },
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error("File upload failed");
      }

      const uploadedUrl = await uploadRes.text();

      // Calculate version number
      const existingFiles = fileType === "evidence" 
        ? evidenceFiles[section]?.[questionId] || []
        : policyFiles[section]?.[questionId] || [];
      
      const version = existingFiles.length + 1;

      const fileMetadata = {
        name: file.name,
        url: uploadedUrl,
        size: file.size,
        type: file.type,
        version: version,
        uploadedAt: new Date().toISOString(),
        uploadedBy: userId,
        fileCategory: fileType,
        questionId: questionId,
        section: section,
      };

      // Update backend metadata
      await saveFileMetadataToBackend(section, fileMetadata, fileType);

      // Update local state
      if (fileType === "evidence") {
        setEvidenceFiles((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [questionId]: [...(prev[section]?.[questionId] || []), fileMetadata],
          },
        }));
      } else {
        setPolicyFiles((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [questionId]: [...(prev[section]?.[questionId] || []), fileMetadata],
          },
        }));
      }

      console.log(`✅ File uploaded: ${file.name} (${fileType})`);
      
      return fileMetadata;

    } catch (error) {
      console.error("❌ Error uploading file:", error);
      return null;
    }
  }, [userId, evidenceFiles, policyFiles]);

  // ==================== SAVE FILE METADATA ====================
  const saveFileMetadataToBackend = async (section, fileMetadata, fileType) => {
    try {
      const payload = {
        userId,
        section,
        fileType,
        fileMetadata,
        timestamp: new Date().toISOString(),
      };

      await axios.post(ISO_ENDPOINTS.saveFileMetadata, payload, {
        headers: {
          "Content-Type": "application/json",
          Region: getUserRegion(),
        },
      });

      console.log("✅ File metadata saved to backend");
      
    } catch (error) {
      console.error("❌ Error saving file metadata:", error);
    }
  };

  // ==================== DELETE FILE ====================
  const deleteISOFile = useCallback(async (section, questionId, fileName, fileType) => {
    if (!userId || !section || !fileName) {
      console.warn("⚠️ Missing required parameters for file deletion");
      return;
    }

    try {
      await axios.delete(ISO_ENDPOINTS.deleteFile, {
        params: { userId, section, questionId, fileName, fileType },
        headers: { Region: getUserRegion() },
      });

      // Update local state
      if (fileType === "evidence") {
        setEvidenceFiles((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [questionId]: (prev[section]?.[questionId] || []).filter(
              (f) => f.name !== fileName
            ),
          },
        }));
      } else {
        setPolicyFiles((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [questionId]: (prev[section]?.[questionId] || []).filter(
              (f) => f.name !== fileName
            ),
          },
        }));
      }

      console.log(`✅ File deleted: ${fileName}`);
      
    } catch (error) {
      console.error("❌ Error deleting file:", error);
    }
  }, [userId]);

  // ==================== CALCULATE SCORE ====================
  const calculateAndUpdateScore = (section, questionId, answer) => {
    setIsoScores((prev) => {
      const sectionScore = Object.values(isoResponses[section] || {}).filter(
        (r) => r.answer === 1
      ).length + (answer === 1 ? 1 : 0);

      const overall = Object.keys(isoResponses).reduce((total, sec) => {
        if (sec === section) return total + sectionScore;
        return total + Object.values(isoResponses[sec] || {}).filter(
          (r) => r.answer === 1
        ).length;
      }, 0);

      return {
        ...prev,
        [section]: sectionScore,
        overall: overall,
      };
    });
  };

  // ==================== LOCALSTORAGE BACKUP ====================
  const saveToLocalStorage = (userId, data) => {
    try {
      localStorage.setItem(`iso_data_${userId}`, JSON.stringify({
        ...data,
        timestamp: new Date().toISOString(),
      }));
      console.log("💾 ISO data saved to localStorage");
    } catch (error) {
      console.error("❌ Error saving to localStorage:", error);
    }
  };

  const loadFromLocalStorage = (userId) => {
    try {
      const saved = localStorage.getItem(`iso_data_${userId}`);
      if (saved) {
        const data = JSON.parse(saved);
        setIsoResponses(data.responses || {});
        setEvidenceFiles(data.evidenceFiles || {});
        setPolicyFiles(data.policyFiles || {});
        setIsoScores(data.scores || {});
        setDataLoaded(true);
        console.log("✅ ISO data loaded from localStorage");
      }
    } catch (error) {
      console.error("❌ Error loading from localStorage:", error);
    }
  };

  // ==================== CLEAR SECTION DATA ====================
  const clearSectionData = (section) => {
    setIsoResponses((prev) => ({ ...prev, [section]: {} }));
    setEvidenceFiles((prev) => ({ ...prev, [section]: {} }));
    setPolicyFiles((prev) => ({ ...prev, [section]: {} }));
  };

  // Context Value
  const value = {
    // State
    userId,
    userData,
    isoResponses,
    evidenceFiles,
    policyFiles,
    isoScores,
    loading,
    dataLoaded,

    // Methods
    fetchAllISOData,
    saveISOResponse,
    uploadISOFile,
    deleteISOFile,
    clearSectionData,
  };

  return <ISOContext.Provider value={value}>{children}</ISOContext.Provider>;
};

// Hook to use ISO Context
export const useISO = () => {
  const context = useContext(ISOContext);
  if (!context) {
    throw new Error("useISO must be used within an ISOProvider");
  }
  return context;
};
